"use client";

import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/utils/cn";
import React from "react";
import { HeroCarousel } from "./hero-carousel";
import { DynamicHeroPreferencesSection } from "./lazy-client-components";
import { SearchSection } from "./search";

const statistics = [
	{
		value: (
			<>
				<span className="text-base">₹</span>
				<span>2,000</span>
			</>
		),
		suffix: "cr+",
		label: "Worth homes sold",
	},
	{ value: "8,000", suffix: "+", label: "Satisfied customers" },
	{ value: "2 Lakh", suffix: "+", label: "Professional visits" },
	{ value: "4.6", suffix: "/5", label: "Google ratings" },
];

export const HeroSection: React.FC = () => {
	const { isAuthenticated, isLoading: authLoading } = useAuth();

	return (
		<section className="w-full min-h-[calc(100vh-64px)] md:min-h-[calc(100vh-80px)] bg-background mx-auto overflow-hidden transform-gpu">
			{/* Background Shape */}
			<div
				className="absolute top-0 right-0 h-[300px] sm:h-[400px] md:h-[500px] lg:h-full z-10 pointer-events-none transform-gpu"
				style={{
					width: "90dvw",
					transform: "translateX(calc(50dvw - 50%)) translateZ(0)",
					color: "oklch(var(--accent) / 0.4)",
					willChange: "auto", // Don't animate the background shape
				}}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="995"
					height="761"
					viewBox="0 0 995 761"
					fill="currentColor"
					className="absolute top-0 right-0 w-auto h-full min-w-[400px] md:min-w-[600px] lg:min-w-[995px]"
					preserveAspectRatio="xMaxYMin meet"
				>
					<path
						opacity="0.7"
						d="M242 294C-13.6122 245.186 -23.1432 98.5289 19.7281 0H995V760.5C758 760.5 567 702 497 584.5C427 467 497.613 342.814 242 294Z"
					/>
				</svg>
			</div>

			{/* Main Content Container */}
			<div className="relative z-30 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8 flex flex-col md:flex-row md:items-start md:gap-8">
				<div className="flex flex-col h-full flex-1 pt-4 md:pt-12 lg:pt-16">
					{/* Left Content */}
					{/* z-40 ensures it is above carousel which is z-20 */}
					<div className="flex-1 flex flex-col relative z-40">
						{/* Main Heading */}
						<div className="mb-4 md:mb-6">
							<h1
								className={cn(
									"text-3xl md:text-5xl font-semibold leading-tight md:leading-none mb-2 text-center md:text-left",
									"bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent",
									"animate-[fade-in-up_0.8s_ease-out_forwards]",
								)}
							>
								Buy or Sell
							</h1>
							<h2
								className={cn(
									"text-xl md:text-2xl font-semibold text-foreground leading-tight md:leading-none text-center md:text-left",
									"animate-[fade-in-up_1s_ease-out_forwards]",
								)}
							>
								property easily with Settlin
							</h2>
						</div>

						{/* Subtitle */}
						<p className="text-base md:text-lg font-bold mb-6 max-w-[635px] text-center md:text-left">
							<span className="text-primary-700">
								Settl
								<span className="text-xs text-primary-700/80 font-normal">-</span>
								in or Settl
								<span className="text-xs text-primary-700/80 font-normal">-</span>
								it.{" "}
							</span>
							<span className="text-foreground">Search to Sale-deed</span>
						</p>

						{/* Mobile Carousel */}
						<div className="block md:hidden w-full">
							<HeroCarousel />
						</div>

						<div className="mb-8">
							<SearchSection isLoggedIn={!authLoading && isAuthenticated} />
						</div>

						{!authLoading && isAuthenticated && (
							<div className="mb-8">
								{/* Preferences Section - Lazy loaded for better LCP */}
								<DynamicHeroPreferencesSection />
							</div>
						)}

						{/* Statistics */}
						<div className="mb-8 -z-10" id="statistics">
							<div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-6 xl:gap-6">
								{statistics.map((stat, index) => (
									<div
										key={index}
										className="text-center md:text-left transform-gpu"
										role="group"
										aria-label={stat.label}
									>
										<div className="flex items-baseline justify-center md:justify-start">
											<div className="flex items-baseline whitespace-nowrap">
												<span className="text-2xl sm:text-3xl font-bold text-foreground">{stat.value}</span>
												<span className="text-sm sm:text-base font-bold text-primary-700 ml-1">{stat.suffix}</span>
											</div>
										</div>
										<p className="text-xs sm:text-sm text-foreground mt-1 md:mt-2 leading-tight">{stat.label}</p>
									</div>
								))}
							</div>
						</div>
					</div>
					{/* Desktop & Tablet Carousel */}
				</div>
				<div className="hidden md:flex flex-1 flex-col max-w-[600px] align-center pt-4 md:pt-12 lg:pt-16">
					<HeroCarousel />
				</div>
			</div>
		</section>
	);
};
